/**
 * The "Referrals" section of the admin monthly billing page: every user someone
 * brought to the site, grouped under that person, with what each one was billed
 * in the selected month.
 *
 * Plain functions, so they run under plain node (tests/unit/referralGroups.test.js).
 * `referrals` come from /billing/referrals, `rows` from /billing/monthly-summary
 * (already filtered to the selected month, one row per manager + ride).
 */

const round2 = n => Math.round(n * 100) / 100;

/**
 * @returns {Array<{referrer_id, referrer_name, referrer_email, total, currency,
 *   users: Array<{user_id, name, email, referred_at, total, currency, line_count}>}>}
 *   Referrers with the highest month total first; a referred user with no billing
 *   this month is still listed, at 0.
 */
function buildReferralGroups(referrals, rows) {
  const usage = {};
  for (const row of rows || []) {
    const id = row.manager_id;
    if (!usage[id]) usage[id] = { total: 0, currency: row.currency || null, line_count: 0 };
    usage[id].total += row.ride_amount || 0;
    usage[id].line_count += 1;
    if (!usage[id].currency && row.currency) usage[id].currency = row.currency;
  }

  const groups = {};
  for (const ref of referrals || []) {
    const rid = ref.referrer_id;
    if (!groups[rid]) {
      groups[rid] = {
        referrer_id: rid,
        referrer_name: ref.referrer_name,
        referrer_email: ref.referrer_email,
        total: 0,
        currency: null,
        users: [],
      };
    }
    const used = usage[ref.referred_user_id];
    const total = used ? round2(used.total) : 0;
    groups[rid].users.push({
      user_id: ref.referred_user_id,
      name: ref.referred_name,
      email: ref.referred_email,
      referred_at: ref.created_at || null,
      total,
      currency: used ? used.currency : null,
      line_count: used ? used.line_count : 0,
    });
    groups[rid].total = round2(groups[rid].total + total);
    if (!groups[rid].currency && used && used.currency) groups[rid].currency = used.currency;
  }

  const result = Object.values(groups);
  for (const group of result) group.users.sort((a, b) => b.total - a.total);
  result.sort((a, b) => b.total - a.total);
  return result;
}

module.exports = { buildReferralGroups };
